import React, { useMemo, useState } from "react";
import { Dialog, DialogActionsBar } from "@progress/kendo-react-dialogs";
import { Button } from "@progress/kendo-react-buttons";
import { Label } from "@progress/kendo-react-labels";
import { toast } from "react-toastify";
import SubmitButton from "components/common/SubmitButton";
import MattersCombobox from "pages/Postings/MattersComboBox";
import { Tile } from "components/UIComponents/Tile";
import DataList from "components/MatterDetails/DataList";
import { useActiveFeeEarner } from "services/userService";
import styles from "components/Activities/CaseActivity.module.scss";

const Row = ({ label, value }) => (
  <DataList
    text={label}
    value={value ?? " "}
    commercial
    className={styles.moveFieldRow}
  />
);

const formatNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const formatDate = (v) => {
  if (!v) return "-";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "-" : d.toLocaleDateString("en-GB");
};

const MoveCopyActivityModal = ({
  sourceMatter,
  activity,
  onClose,
  onConfirm,
  isProcessing = false,
}) => {
  const [selectedMatter, setSelectedMatter] = useState(null);
  const [pendingMode, setPendingMode] = useState(null);
  const { data: feeEarners } = useActiveFeeEarner();

  const sourceMatterId = sourceMatter?.matterId ?? activity?.matterId;
  const isBilled = Boolean(activity?.isBilled);

  const feDisplay = useMemo(() => {
    const map = {};
    (feeEarners || []).forEach((u) => {
      map[u.userRef] = u.displayname;
    });
    return (ref) => (ref ? (map[ref] ? `${ref} - ${map[ref]}` : ref) : "-");
  }, [feeEarners]);

  const handleAction = (mode) => {
    if (!selectedMatter?.matterId) {
      toast.error("Please select a matter to move to.");
      return;
    }
    if (String(selectedMatter.matterId) === String(sourceMatterId)) {
      toast.error("Please select a matter different from the current one.");
      return;
    }
    setPendingMode(mode);
    onConfirm?.(mode, selectedMatter);
  };

  return (
    <div className={styles.moveActivityDialog}>
      <Dialog title="Move Matter" onClose={onClose} className="dialog-sm">
        <div
          className="d-flex p-4"
          style={{ flex: 1, minHeight: 0, gap: 20, overflow: "auto" }}>
          <div className="d-flex flex-column" style={{ flex: 1, minWidth: 0 }}>
            <Tile className="py-3 px-4 mb-3" heading="Current Matter">
              <Row label="Matter Ref:" value={sourceMatter?.matterRef} />
              <Row label="Matter Details:" value={sourceMatter?.details} />
            </Tile>

            <Tile
              className="py-3 px-4"
              heading="Activity Details"
              minHeight={0}
              style={{ flex: 1 }}>
              <Row label="Type:" value={activity?.type} />
              <Row label="Date:" value={formatDate(activity?.date)} />
              <Row label="Fee Earner:" value={feDisplay(activity?.feeEarnerRef)} />
              <Row label="Details:" value={activity?.details} />
            </Tile>
          </div>

          <div className="d-flex flex-column" style={{ flex: 1, minWidth: 0 }}>
            <Tile className="py-3 px-4 mb-3" heading="New Matter">
              <div className={`d-flex flex-column ${styles.moveFieldRow}`}>
                <Label className="label mb-1">Matter</Label>
                <MattersCombobox
                  value={selectedMatter}
                  onChangematter={(e) => setSelectedMatter(e?.target?.value ?? null)}
                  style={{ width: "100%" }}
                  className="w-100"
                />
              </div>
              <Row label="Matter Ref:" value={selectedMatter?.matterRef} />
              <Row
                label="Fee Earner:"
                value={
                  selectedMatter ? feDisplay(selectedMatter?.feeEarnerRef) : "-"
                }
              />
              <Row label="Matter Details:" value={selectedMatter?.details} />
            </Tile>

            <Tile className="py-3 px-4" heading="Time Details">
              <Row label="Unit:" value={formatNumber(activity?.units)} />
              <Row label="Rate:" value={formatNumber(activity?.rate)} />
              <Row label="Value:" value={formatNumber(activity?.value)} />
              <Row label="Billed:" value={isBilled ? "Yes" : "No"} />
            </Tile>
          </div>
        </div>

        <DialogActionsBar>
          <div className="dialog-footer w-100 d-flex justify-content-between align-items-center gap-2 px-4">
            <Button
              className="common-btn bgBtnSecondary"
              onClick={onClose}
              disabled={isProcessing}>
              Cancel
            </Button>
            <div className="d-flex gap-2">
              <SubmitButton
                type="button"
                className="bgBtnSecondary"
                onClick={() => handleAction("copy")}
                isLoading={isProcessing && pendingMode === "copy"}
                disabled={isProcessing || !selectedMatter?.matterId}>
                Copy Activity
              </SubmitButton>
              <SubmitButton
                type="button"
                className=""
                onClick={() => handleAction("move")}
                isLoading={isProcessing && pendingMode === "move"}
                disabled={isProcessing || isBilled || !selectedMatter?.matterId}
                title={
                  isBilled
                    ? "This activity is billed and cannot be moved."
                    : undefined
                }>
                Move Activity
              </SubmitButton>
            </div>
          </div>
        </DialogActionsBar>
      </Dialog>
    </div>
  );
};

export default MoveCopyActivityModal;
